import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { IoService } from '../../../services/io/io.service';
import { AppComponent } from 'src/app/app.component';
import { DropdownOptionGroup, DropdownOption, DropdownSelectionMode } from '../../shared/interfaces/option-interfaces';
import { DialogOption } from '../../shared/ui/dialog/dialog-options';

// Friendly presentation of the four stored option group types. The stored type
// strings are consumed by ISE and CloudAPI and must never change.
export const OPTION_GROUP_TYPES = [
  {
    type: 'components',
    label: 'Recipe / Ingredients',
    example: 'Always included — e.g. the bun and patty in a burger. Not shown at the register.',
  },
  {
    type: 'select',
    label: 'Pick One',
    example: 'Customer picks exactly one — e.g. which meat on a sandwich.',
  },
  {
    type: 'multiple',
    label: 'Pick Any',
    example: 'Customer picks any they want, optionally between a min and max — e.g. toppings.',
  },
  {
    type: 'multipleMultiple',
    label: 'Pick With Quantities',
    example: 'Customer picks how many of each — e.g. extra espresso shots.',
  },
];

// One breadcrumb level: the item being edited plus its working copy of option
// groups. Prices are held in dollars while editing and converted back to cents
// on save. Existing keys ride along untouched; the server generates keys for
// new groups/options.
interface EditorLevel {
  accountId: string;
  account: any;
  accounts: any;
  groups: any[];
  activeGroupIndex: number;
  cleanSnapshot: string;
  showAdvanced: boolean;
  addingGroup: boolean;
  saving: boolean;
  errors: string[];
  preview: { [previewKey: string]: number };
}

@Component({
  selector: 'app-inventory-options',
  templateUrl: './inventory-options.component.html',
  styleUrls: ['./inventory-options.component.scss']
})
export class InventoryOptionsComponent implements OnInit {

  groupTypes = OPTION_GROUP_TYPES;
  levels: EditorLevel[] = [];
  loading = false;
  loadError: string;

  itemSearchField: DropdownOptionGroup = {
    key: 'options',
    name: null,
    options: new Array<DropdownOption>(),
    selection: new Array<DropdownOption>(),
    opposingIdKey: null,
    selectMode: DropdownSelectionMode.None
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<InventoryOptionsComponent>,
    private ioService: IoService,
  ) { }

  ngOnInit() {
    this.openLevel(this.data.documentId);
  }

  get level(): EditorLevel {
    return this.levels[this.levels.length - 1];
  }

  get activeGroup(): any {
    if (!this.level || this.level.activeGroupIndex == null) {
      return null;
    }
    return this.level.groups[this.level.activeGroupIndex] || null;
  }

  // ----- Loading / model -----

  openLevel = async (accountId: string, replaceCurrent = false) => {
    this.loading = true;
    this.loadError = null;
    try {
      const account: any = await this.ioService.post('/inventory/getInventoryAccount', {
        accountId,
      });

      const newLevel: EditorLevel = {
        accountId,
        account,
        accounts: account.accounts || {},
        groups: this.buildEditModel(account),
        activeGroupIndex: null,
        cleanSnapshot: null,
        showAdvanced: false,
        addingGroup: false,
        saving: false,
        errors: [],
        preview: {},
      };
      newLevel.cleanSnapshot = JSON.stringify(this.serializeGroups(newLevel.groups));
      if (newLevel.groups.length > 0) {
        newLevel.activeGroupIndex = 0;
      }

      if (replaceCurrent) {
        this.levels[this.levels.length - 1] = newLevel;
      } else {
        this.levels.push(newLevel);
      }
      this.resetPreview();
    } catch (e) {
      this.loadError = e?.error?.msg || 'Unable to load this item.';
    }
    this.loading = false;
  }

  buildEditModel(account: any): any[] {
    const groups = [];
    for (const optionGroup of (account.options || [])) {
      groups.push({
        key: optionGroup.key,
        type: optionGroup.type || 'select',
        name: optionGroup.name,
        min: optionGroup.min ?? null,
        max: optionGroup.max ?? null,
        options: (optionGroup.options || []).map((option) => ({
          key: option.key,
          accountId: option.accountId,
          name: option.name,
          qty: option.qty ?? 1,
          price: option.price != null ? +(option.price / 100).toFixed(2) : null,
          default: option.default ?? null,
        })),
      });
    }
    return groups;
  }

  serializeGroups(groups: any[]): any[] {
    return groups.map((group) => ({
      key: group.key || null,
      type: group.type,
      name: group.name || null,
      min: group.min != null && group.min !== '' ? +group.min : null,
      max: group.max != null && group.max !== '' ? +group.max : null,
      options: group.options.map((option) => ({
        key: option.key || null,
        accountId: option.accountId,
        name: option.name || null,
        qty: option.qty != null && option.qty !== '' && !isNaN(option.qty) ? +option.qty : 1,
        price: this.dollarsToCents(option.price),
        default: option.default != null && option.default !== '' && +option.default !== 0 ? +option.default : null,
      })),
    }));
  }

  dollarsToCents(value: any): number {
    if (value == null || value === '' || isNaN(value)) {
      return null;
    }
    return Math.round(parseFloat(value) * 100);
  }

  isDirty(level: EditorLevel = this.level): boolean {
    if (!level || level.cleanSnapshot == null) {
      return false;
    }
    return JSON.stringify(this.serializeGroups(level.groups)) !== level.cleanSnapshot;
  }

  typeLabel(type: string): string {
    const groupType = OPTION_GROUP_TYPES.find((t) => t.type === type);
    return groupType ? groupType.label : type;
  }

  // ----- Groups -----

  setActiveGroup(index: number) {
    this.level.addingGroup = false;
    this.level.activeGroupIndex = index;
    this.itemSearchField.options = new Array<DropdownOption>();
    this.resetPreview();
  }

  startAddGroup() {
    this.level.addingGroup = true;
  }

  addGroup(type: string) {
    const group: any = {
      key: null,
      type,
      name: type === 'components' ? 'Components' : '',
      min: null,
      max: null,
      options: [],
    };
    this.level.groups.push(group);
    this.setActiveGroup(this.level.groups.length - 1);
  }

  removeGroup(index: number) {
    const group = this.level.groups[index];
    const label = group.type === 'components' ? 'the Recipe / Ingredients group' : `"${group.name || this.typeLabel(group.type)}"`;
    const confirmRef = AppComponent.showMessage('Remove Group', `Remove ${label} and its options from this item?`, ['Remove', 'Cancel'], false);
    confirmRef.afterClosed().subscribe((result: DialogOption) => {
      if (!result || result.value !== 'Remove') {
        return;
      }
      this.level.groups.splice(index, 1);
      if (this.level.activeGroupIndex === index) {
        this.level.activeGroupIndex = this.level.groups.length > 0 ? 0 : null;
      } else if (this.level.activeGroupIndex > index) {
        this.level.activeGroupIndex--;
      }
      this.resetPreview();
    });
  }

  groupChanged() {
    this.resetPreview();
  }

  // Reorder only changes the display order (here and at the register). Purchase
  // selections reference groups by key, not position, so this is always safe.
  dropGroup(event: CdkDragDrop<any[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const activeGroup = this.activeGroup;
    moveItemInArray(this.level.groups, event.previousIndex, event.currentIndex);
    if (activeGroup) {
      this.level.activeGroupIndex = this.level.groups.indexOf(activeGroup);
    }
  }

  dropOption(event: CdkDragDrop<any[]>) {
    if (event.previousIndex === event.currentIndex || !this.activeGroup) {
      return;
    }
    moveItemInArray(this.activeGroup.options, event.previousIndex, event.currentIndex);
    this.resetPreview();
  }

  hasComponentsGroup(): boolean {
    return this.level && this.level.groups.some((g) => g.type === 'components' && g.options.length > 0);
  }

  // ----- Options within the active group -----

  searchItems = async (searchText: string) => {
    if (!searchText || searchText.length < 3 || !this.activeGroup) {
      return;
    }
    const account = this.level.account;
    const parameters: any = {
      active: true,
      type: 'inventory',
      locationId: account.locationId,
      search: searchText,
      limit: 10,
      omit: this.activeGroup.options.map((o) => o.accountId).concat([account._id]),
      returnCategoryName: true,
    };
    if (account.subtype === 'selector') {
      parameters.subtype = 'retail';
    }
    const results = await this.ioService.post('/account/getAccounts', parameters) as Array<any>;
    this.itemSearchField.options = results.map((r) => Object.assign(new DropdownOption(r._id, r.name), r));
  }

  itemSelected() {
    if (this.itemSearchField.selection && this.itemSearchField.selection.length > 0) {
      this.addOption(this.itemSearchField.selection[0]);
      this.itemSearchField.selection = new Array<DropdownOption>();
    }
  }

  createNewItem = async (name: string) => {
    const account = this.level.account;
    // New items are retail products kept off the register (ise: false) — they
    // exist to be sold as part of this item, but can be promoted later.
    const newAccountConfig: any = {
      type: 'inventory',
      locationId: account.locationId,
      subtype: 'retail',
      name,
      unitOfMeasure: 'EA',
      ise: false,
    };
    const newAccount: any = await this.ioService.post('/account/addAccount', newAccountConfig);
    this.addOption(newAccount);
  }

  addOption(childAccount: any) {
    this.level.accounts[childAccount._id] = childAccount;
    this.activeGroup.options.push({
      key: null,
      accountId: childAccount._id,
      name: childAccount.shortName ?? childAccount.name,
      qty: 1,
      price: null,
      default: null,
    });
    this.resetPreview();
  }

  removeOption(index: number) {
    this.activeGroup.options.splice(index, 1);
    this.resetPreview();
  }

  toggleDefault(option: any) {
    option.default = option.default ? null : 1;
    // Pick One: only a single option can be the default.
    if (option.default && this.activeGroup.type === 'select') {
      for (const other of this.activeGroup.options) {
        if (other !== option) {
          other.default = null;
        }
      }
    }
    this.resetPreview();
  }

  childAccount(option: any): any {
    return this.level.accounts[option.accountId] || null;
  }

  // ----- Cost estimates (display only — the server computes the stored values on save) -----

  childCostRange(option: any): { low: number, high: number } {
    const child = this.childAccount(option);
    if (!child) {
      return { low: 0, high: 0 };
    }
    if (child.cost) {
      return { low: child.cost, high: child.cost };
    }
    if (child.calculatedCost) {
      return { low: child.calculatedCost.low || 0, high: child.calculatedCost.high || 0 };
    }
    return { low: 0, high: 0 };
  }

  optionCostRange(option: any): { low: number, high: number } {
    const range = this.childCostRange(option);
    const qty = option.qty || 0;
    return { low: range.low * qty, high: range.high * qty };
  }

  groupCostRange(group: any): { low: number, high: number } {
    const ranges = group.options.map((o) => this.optionCostRange(o));
    if (ranges.length === 0) {
      return { low: 0, high: 0 };
    }
    switch (group.type) {
      case 'components':
        return {
          low: ranges.reduce((sum, r) => sum + r.low, 0),
          high: ranges.reduce((sum, r) => sum + r.high, 0),
        };
      case 'select':
        return {
          low: Math.min(...ranges.map((r) => r.low)),
          high: Math.max(...ranges.map((r) => r.high)),
        };
      case 'multiple': {
        const minCount = group.min || 0;
        const maxCount = Math.min(group.max || ranges.length, ranges.length);
        const lows = ranges.map((r) => r.low).sort((a, b) => a - b);
        const highs = ranges.map((r) => r.high).sort((a, b) => b - a);
        return {
          low: lows.slice(0, minCount).reduce((a, b) => a + b, 0),
          high: highs.slice(0, maxCount).reduce((a, b) => a + b, 0),
        };
      }
      case 'multipleMultiple': {
        const minCount = group.min || 0;
        const maxCount = group.max || ranges.length;
        return {
          low: Math.min(...ranges.map((r) => r.low)) * minCount,
          high: Math.max(...ranges.map((r) => r.high)) * maxCount,
        };
      }
    }
    return { low: 0, high: 0 };
  }

  priceBelowCost(option: any): boolean {
    if (option.price == null || option.price === '' || isNaN(option.price)) {
      return false;
    }
    return (+option.price * 100) < this.optionCostRange(option).high;
  }

  // ----- Register preview (mirrors ISE's rendering rules) -----

  previewKey(option: any): string {
    return option.key || 'new-' + option.accountId;
  }

  previewQty(option: any): number {
    return this.level.preview[this.previewKey(option)] || 0;
  }

  previewSelectedCount(group: any): number {
    return group.options.reduce((sum, o) => sum + this.previewQty(o), 0);
  }

  resetPreview() {
    if (!this.level) {
      return;
    }
    this.level.preview = {};
    const group = this.activeGroup;
    if (!group || group.type === 'components') {
      return;
    }
    // ISE pre-selects defaults when the item is added to a sale.
    for (const option of group.options) {
      if (option.default > 0) {
        this.level.preview[this.previewKey(option)] = group.type === 'multipleMultiple' ? +option.default : 1;
      }
    }
  }

  previewClick(group: any, option: any) {
    const key = this.previewKey(option);
    switch (group.type) {
      case 'select':
        // Picking any option clears the group first — exactly one wins.
        this.level.preview = {};
        this.level.preview[key] = 1;
        break;
      case 'multiple':
        if (this.previewQty(option) > 0) {
          delete this.level.preview[key];
        } else if (!group.max || this.previewSelectedCount(group) < group.max) {
          this.level.preview[key] = 1;
        }
        break;
    }
  }

  previewStep(group: any, option: any, delta: number) {
    const key = this.previewKey(option);
    const current = this.previewQty(option);
    if (delta > 0 && group.max && this.previewSelectedCount(group) >= group.max) {
      return;
    }
    const next = Math.max(0, current + delta);
    if (next === 0) {
      delete this.level.preview[key];
    } else {
      this.level.preview[key] = next;
    }
  }

  previewCanAdd(group: any): boolean {
    return !group.max || this.previewSelectedCount(group) < group.max;
  }

  // ----- Drill-down / breadcrumbs -----

  // Hands the item off to detail-view, which closes this dialog and routes to
  // the basic item editor. The dialog's URL state stays in browser history, so
  // the Back button returns the user here.
  openItem = async (option: any) => {
    const resolution = await this.resolveDirty();
    if (resolution === 'cancel') {
      return;
    }
    this.dialogRef.close({ openItemId: String(option.accountId) });
  }

  goBack = async (levelIndex: number) => {
    if (levelIndex >= this.levels.length - 1) {
      return;
    }
    const resolution = await this.resolveDirty();
    if (resolution === 'cancel') {
      return;
    }
    this.levels.splice(levelIndex + 1);
    // Re-fetch so parent shows costs recomputed from any child saves.
    await this.openLevel(this.level.accountId, true);
  }

  // Prompts to save or discard unsaved changes on the current level.
  // Returns 'cancel' if the user backed out (callers should abort).
  resolveDirty = async (): Promise<string> => {
    if (!this.isDirty()) {
      return 'clean';
    }
    const dialogRef = AppComponent.showMessage(
      'Unsaved Changes',
      `Save your changes to ${this.level.account.name} first?`,
      ['Save', 'Discard', 'Cancel'],
      false,
    );
    const result: DialogOption = await dialogRef.afterClosed().toPromise();
    if (!result || result.value === 'Cancel') {
      return 'cancel';
    }
    if (result.value === 'Save') {
      const saved = await this.save();
      return saved ? 'save' : 'cancel';
    }
    return 'discard';
  }

  // ----- Save / close -----

  validate(): string[] {
    const errors = [];
    for (const group of this.level.groups) {
      if (group.type !== 'components' && !group.name) {
        errors.push(`Every ${this.typeLabel(group.type)} group needs a name — the customer sees it at the register.`);
      }
      if ((group.type === 'multiple' || group.type === 'multipleMultiple') &&
          group.min != null && group.max != null && group.min !== '' && group.max !== '' &&
          +group.min > +group.max) {
        errors.push(`"${group.name || this.typeLabel(group.type)}": minimum can't be greater than maximum.`);
      }
      for (const option of group.options) {
        if (option.qty != null && option.qty !== '' && +option.qty < 0) {
          errors.push(`"${option.name}": quantity can't be negative.`);
        }
      }
    }
    return errors;
  }

  save = async (): Promise<boolean> => {
    if (this.level.saving) {
      return false;
    }
    this.level.errors = this.validate();
    if (this.level.errors.length > 0) {
      return false;
    }
    this.level.saving = true;
    try {
      await this.ioService.post('/inventory/updateAccountOptions', {
        accountId: this.level.accountId,
        options: this.serializeGroups(this.level.groups),
      });
      // Reload so the model picks up server-generated keys and recomputed costs.
      const activeIndex = this.level.activeGroupIndex;
      await this.openLevel(this.level.accountId, true);
      if (activeIndex != null && activeIndex < this.level.groups.length) {
        this.setActiveGroup(activeIndex);
      }
      AppComponent.showSnack('Options saved.', 'OK', 3000);
      this.level.saving = false;
      return true;
    } catch (e) {
      this.level.errors = [e?.error?.msg || 'Save failed.'];
      this.level.saving = false;
      return false;
    }
  }

  close = async () => {
    const resolution = await this.resolveDirty();
    if (resolution === 'cancel') {
      return;
    }
    this.dialogRef.close();
  }
}
